import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  PlayIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox';
import Progress from '@/components/ui/Progress';
import clsx from 'clsx';

// Mock voices data
const voices = [
  { value: 'RFBMQGMs3dbpbuSvswMJ', label: 'Cath Brunet (fr-CA, female)', gender: 'female', sample: true },
  { value: '2xgQghgxwdXTjTfiIS15', label: 'Gui Nadon (fr-CA, male)', gender: 'male', sample: true },
  { value: 'voice_nicolas', label: 'Nicolas Charbonneaux (fr-CA, male)', gender: 'male', sample: true },
  { value: 'voice_ingrid', label: 'Ingrid Falaise (fr-CA, female)', gender: 'female', sample: true },
  { value: 'voice_frederique', label: 'Frédérique Dufort (fr-CA, female)', gender: 'female', sample: true },
  { value: 'voice_patrice', label: 'Patrice Robitaille (fr-CA, male)', gender: 'male', sample: true },
];

interface UploadFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
}

export default function TTSUploadPage() {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);

  const [options, setOptions] = useState({
    voice_id: '',
    outputMp3: true,
    outputZip: true,
    priority: false,
    skipOcr: false,
  });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find((f) => f.type === 'application/pdf');
    if (pdfFile) {
      setUploadFile({ file: pdfFile, progress: 0, status: 'pending' });
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setUploadFile({ file: files[0], progress: 0, status: 'pending' });
    }
  };

  const removeFile = () => {
    setUploadFile(null);
  };

  const handleSubmit = async () => {
    if (!uploadFile || !options.voice_id) return;

    setIsUploading(true);
    setUploadFile({ ...uploadFile, status: 'uploading' });

    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setUploadFile((prev) => (prev ? { ...prev, progress: i } : null));
    }

    setUploadFile((prev) => (prev ? { ...prev, status: 'complete' } : null));
    setIsUploading(false);

    // Redirect to job detail after short delay
    setTimeout(() => {
      navigate('/tts/jobs/new_job_123');
    }, 500);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Upload New Audiobook</h1>
        <p className="text-slate-500 mt-1">Convert your PDF to audiobook using AI voice synthesis</p>
      </div>

      {/* Upload Zone */}
      <Card>
        <CardBody>
          <div
            className={clsx(
              'relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200',
              dragActive
                ? 'border-primary-400 bg-primary-50'
                : uploadFile
                ? 'border-success-300 bg-success-50'
                : 'border-slate-200 hover:border-primary-300 hover:bg-slate-50'
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploadFile ? (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-white shadow-md flex items-center justify-center">
                  <DocumentIcon className="w-8 h-8 text-danger-500" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{uploadFile.file.name}</p>
                  <p className="text-sm text-slate-500">{formatFileSize(uploadFile.file.size)}</p>
                </div>
                {uploadFile.status === 'uploading' && (
                  <div className="max-w-xs mx-auto">
                    <Progress value={uploadFile.progress} showLabel variant="primary" />
                  </div>
                )}
                {uploadFile.status === 'complete' && (
                  <p className="text-success-500 font-medium">Upload complete!</p>
                )}
                {uploadFile.status === 'pending' && (
                  <Button variant="ghost" size="sm" onClick={removeFile}>
                    <XMarkIcon className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                  <CloudArrowUpIcon className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-lg font-medium text-slate-700 mb-2">
                  Drop your PDF file here
                </p>
                <p className="text-sm text-slate-500 mb-4">or click to browse</p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <p className="text-xs text-slate-400">Maximum file size: 50MB</p>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Options */}
      <Card>
        <CardBody className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-800">Conversion Options</h2>

          {/* Voice Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Voice Selection</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {voices.map((voice) => (
                <div
                  key={voice.value}
                  onClick={() => setOptions({ ...options, voice_id: voice.value })}
                  className={clsx(
                    'relative flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                    options.voice_id === voice.value
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div
                    className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      voice.gender === 'female' ? 'bg-pink-100' : 'bg-blue-100'
                    )}
                  >
                    <SpeakerWaveIcon
                      className={clsx(
                        'w-5 h-5',
                        voice.gender === 'female' ? 'text-pink-500' : 'text-blue-500'
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{voice.label.split(' (')[0]}</p>
                    <p className="text-xs text-slate-500">
                      {voice.label.match(/\((.*?)\)/)?.[1] || ''}
                    </p>
                  </div>
                  {voice.sample && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPlayingVoice(playingVoice === voice.value ? null : voice.value);
                      }}
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                        playingVoice === voice.value
                          ? 'bg-primary-400 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      )}
                    >
                      <PlayIcon className="w-4 h-4" />
                    </button>
                  )}
                  {options.voice_id === voice.value && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Output Format */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Output Format</label>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                label="MP3 (individual files)"
                checked={options.outputMp3}
                onChange={(e) => setOptions({ ...options, outputMp3: e.target.checked })}
              />
              <Checkbox
                label="ZIP (bundled)"
                checked={options.outputZip}
                onChange={(e) => setOptions({ ...options, outputZip: e.target.checked })}
              />
            </div>
          </div>

          {/* Processing Options */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Processing Options</label>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                label="Priority processing"
                description="Process this job before others in queue"
                checked={options.priority}
                onChange={(e) => setOptions({ ...options, priority: e.target.checked })}
              />
              <Checkbox
                label="Skip OCR (clean PDF)"
                description="Skip OCR if your PDF has clean, selectable text"
                checked={options.skipOcr}
                onChange={(e) => setOptions({ ...options, skipOcr: e.target.checked })}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={!uploadFile || !options.voice_id || isUploading}
              isLoading={isUploading}
            >
              <CloudArrowUpIcon className="w-5 h-5 mr-2" />
              Start Conversion
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

